import '.a8{background:transparent;}.ssr-plugin'
import '.x{border:none;}.ssr-plugin'
import '.a9{cursor:pointer;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.aa{gap:24px;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.ab{height:1px;}.ssr-plugin'
import '.ac{align-self:stretch;}.ssr-plugin'
import '.ad{margin:0px;}.ssr-plugin'
import '.ae{justify-content:space-between;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import { FC, useEffect } from 'react'
import { Layout } from './Layout'
import { Navigation } from '../components/Navigation'
import { Text } from '../components/Text'
import { useTheme } from '../providers/useTheme'
import { useAppCtx } from '../providers/useAppCtx'
import { ContestantCard } from '../components/ContestantCard'
import { useIsMobile } from '../hooks/useIsMobile'
import { CartItemsIndicator } from '../components/CartBar'
import { useContentQuery } from '../hooks/useContentQuery'
import { styled } from 'inlines'
import { Button } from '../components/Button'
import { Link, useLocation } from 'wouter'
import {
  replaceMustachesInString,
  useTranslations,
  useMustacheIndex,
} from '../hooks/useTranslations'
import { formatCurrency } from '@/apps/cms/utils/misc'
// @ts-ignore
import fallbackImage from '../assets/img/Square-Heart.png'
import { useScrollToTop } from '../hooks/useScrollToTop'

export type CartProps = {
  'aria-label'?: {
    nav: string
    footer: string
  }
}

export const Cart: FC<CartProps> = ({
  'aria-label': ariaLabelProp = {
    nav: 'Main navigation',
    footer: 'Footer navigation',
  },
}) => {
  const [, setLocation] = useLocation()
  const {
    languages,
    votes,
    setVotes,
    votesCount,
    price,
    currency,
    minVotes = 0,
    maxVotes,
    activeLanguage,
  } = useAppCtx()
  const { data: sequence = {} } = useContentQuery('sequence')
  const { data: navTranslations } = useTranslations('navigation')
  const { data: cartTranslations } = useTranslations('cart')
  const { data: votingTranslations } = useTranslations('voting')
  const mustacheIndex = useMustacheIndex()

  const { colors } = useTheme()
  const isMobile = useIsMobile()

  useScrollToTop()

  const title = replaceMustachesInString(sequence?.prompt, mustacheIndex)

  const contestants =
    sequence?.round.contestants.filter((c) => votes?.[c.$ddi] > 0) || []

  const remainingVotes = maxVotes - votesCount

  const priceLabel = formatCurrency(
    (votesCount * price) / 100,
    currency,
    activeLanguage,
  )

  useEffect(() => {
    if (!votesCount || sequence?.sequenceType !== 'voting') {
      setLocation('/', { replace: true })
    }
  }, [votesCount, sequence, sequence?.sequenceType])

  return (
    <Layout
      style={{
        justifyContent: 'start',
        gap: 48,
        ...(isMobile
          ? {
              padding: 24,
            }
          : {
              padding: '48px 24px',
              alignSelf: 'center',
              maxWidth: 720,
            }),
      }}
    >
      <Layout.Navbar>
        <Navigation
          as="nav"
          aria-label={ariaLabelProp.nav}
          isCartVisible
          isExpandable
          cartItems={votesCount}
          languages={languages}
        />
      </Layout.Navbar>

      <Layout.Top>
        <div
          style={{
            marginBottom: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            width: '100%',
          }}
        >
          <Link href="/">
            <button className="a8 x a9"
            >
              <Text
                variant="display-medium"
                color={colors.primaryAccent}
                onClick={() => {}}
              >
                ← {navTranslations.back_button}
              </Text>
            </button>
          </Link>

          <Text color="gradient" variant="title" isMultiline>
            {title}
          </Text>

          <Text
            variant="subheading-regular"
            color={colors.textSecondary}
            isMultiline
          >
            {votingTranslations.short_payment_rules}
          </Text>

          <CartItemsIndicator
            cartItems={votesCount}
            cartItemsMax={maxVotes}
            cartItemsMin={minVotes}
            color={colors.primaryAccent}
          />

          <Text variant="display-regular" color={colors.textSecondary}>
            {minVotes && votesCount < minVotes
              ? cartTranslations.minVotes_indicator
              : `${cartTranslations.counting_votes_indicator} - ${priceLabel}`}
          </Text>
        </div>
      </Layout.Top>

      <Layout.Middle style={{ width: '100%' }}>
        <div className="a aa i h"
        >
          {contestants
            ?.sort((a, b) => Number(a.$ddi) - Number(b.$ddi))
            ?.map((contestant, index) => {
              const currentVotes = votes?.[contestant.$ddi] || 0
              const maxForThis = currentVotes + remainingVotes

              return (
                <div className="a i aa h"
                  key={contestant.id + '-' + contestant.name + '-' + index}
                >
                  <ContestantCard
                    variant="horizontal"
                    isInverted
                    label={contestant.name}
                    description={contestant.song}
                    votePrice={price}
                    currency={currency}
                    isFocused={false}
                    ddi={contestant.$ddi}
                    isInCart={true}
                    image={contestant.image?.src || fallbackImage}
                    votes={currentVotes}
                    maxVotes={maxForThis}
                    on={{
                      change: (newVotesForThis) => {
                        const parseVotes = (prev) => {
                          if (newVotesForThis === 0 && prev) {
                            const { [contestant.$ddi]: _, ...rest } = prev
                            return rest
                          }
                          return {
                            ...prev,
                            [contestant.$ddi]: newVotesForThis,
                          }
                        }

                        setVotes(parseVotes(votes))
                      },
                    }}
                  />

                  <hr className="ab ac x ad"
                    style={{
                      background: `var(--Outlines, ${colors.border})`,}}
                  />
                </div>
              )
            })}

          <div className="ae a d h"
          >
            <Text variant="heading-bold" color={colors.textPrimary}>
              {cartTranslations.total_text}
            </Text>
            <Text variant="heading-bold" color={colors.textPrimary}>
              {priceLabel}
            </Text>
          </div>
        </div>
      </Layout.Middle>

      <Layout.Bottom
        style={{
          display: 'flex',
          gap: 24,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {votesCount > 0 ? (
          <Link href="/checkout">
            <Button
              isDisabled={minVotes ? votesCount < minVotes : false}
              id="cart-link-checkout"
              label={cartTranslations.go_to_checkout_button}
              trailIcon="arrow"
              color={colors.background}
              style={{ minWidth: isMobile ? '100%' : 'auto' }}
            />
          </Link>
        ) : (
          <Link href="/">
            <Button
              id="cart-link-home"
              label="Add votes"
              leadIcon="add"
              color={colors.background}
              style={{ width: isMobile ? '100%' : 'auto' }}
            />
          </Link>
        )}
        {minVotes && votesCount < minVotes ? (
          <Text
            style={{
              fontSize: isMobile ? 13 : 14,
              color: colors.textSecondary,
              textAlign: isMobile ? 'center' : 'left',
              alignSelf: 'center',
            }}
          >
            {cartTranslations.minVotes_indicator}
          </Text>
        ) : (
          <></>
        )}
      </Layout.Bottom>

      <Layout.Footer>
        <Navigation
          as="footer"
          aria-label={ariaLabelProp.footer}
          languages={languages}
        />
      </Layout.Footer>
    </Layout>
  )
}
